import { getProjectDisplayName } from "@/helper/models/project-display-helper"
import {
  computed,
  effectScope,
  inject,
  nextTick,
  onMounted,
  ref,
  shallowRef,
  watch,
  type Ref
} from "vue"
import { storeToRefs } from "pinia"
import ModelManager from "@/services/model-manager"
import { Location } from "@/models/location.model"
import { FileRelation } from "@/models/file/file-relation.model"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import { useLocationStore } from "@/store/locations.store"
import { useKnownBusinessEntitiesContactsStore } from "@/store/known-business-entities-contacts.store"
import { useProjectStore } from "@/store/projects.store"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store"
import { useModelFormStore } from "@/store/model-forms.store"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model"
import { Project } from "@/models/project.model"
import { KnownBusinessEntity } from "@/models/known-business-entity.model"
import { Person } from "@/models/person.model"
import { ModelFactory } from "@/factories/model-factory"
import { BaseModel } from "@/models/base.model"
import { useAuthStore } from "@/store/auth.store"
import {
  closeMentionDetail,
  mentionDetail,
  openMentionDetail,
  shouldUseReadOnlyMentionDetailModal
} from "@/composables/mention/use-mention-detail"
import { Bus } from "@/services/bus"
import { v4 as uuidv4 } from "uuid"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import {
  fetchSavedLocations,
  filterSavedLocationSuggestions,
  searchLocationSuggestions,
  mergeFirstSuggestionStreetWithHouseNumber,
  shouldApplyFirstSuggestionStreet,
  useSuggestionList,
  type LocationSuggestion
} from "@/composables/location/use-location-autocomplete"
import { fuzzySearchCollection } from "@/helper/collection-helper"
import { normalizeDecimal } from "@/helper/number-helper"
import type { RentalIntake } from "@/models/rental-intake.model"
import type RentalIntakeController from "@/controllers/model-controllers/rental-intake-controller"
import { useRentalIntakeStore } from "@/store/rental-intake.store"
import { unitToAlternativeName } from "@/helper/enums/unit-helper"
import { isPopulated, lazyFieldId } from "@/models/types/lazy-field"
import { compareProjectsByRelation } from "@/helper/models/project-relation-helper"
import {
  stripCountryFromLocationText,
  LOCATION_POINTER_HOVER_TITLE
} from "@/helper/location/location-helpers"

// --- Keypress triggers (location open/close) ---

const RECENT_KEY_LIMIT = 3
const LOCATION_MENTION_CLICK_DELAY_MS = 250

export function pushRecentKey(keys: string[], key: string): string[] {
  return [...keys, key].slice(-RECENT_KEY_LIMIT)
}

export function shouldClearRecentKeys(key: string): boolean {
  return key === "Backspace" || key === "Delete"
}

/** The last two keypresses are space; a third trailing space does not count. */
export function isDoubleSpaceGesture(keys: string[]): boolean {
  if (keys.length < 2) return false
  if (keys[keys.length - 1] !== " " || keys[keys.length - 2] !== " ") return false
  return keys.length < 3 || keys[keys.length - 3] !== " "
}

const LOCATION_TRIGGER = "/"
const queuedLocationMentionLoads = new Set<string>()
const locationMentionLoadPromises = new Map<string, Promise<void>>()
const locationMentionLoadResolvers = new Map<string, () => void>()
const loadedLocationMentionIds = new Set<string>()
let locationMentionLoadController: LocationController | undefined
let locationMentionLoadScheduled = false

function scheduleLocationMentionLoads() {
  if (locationMentionLoadScheduled) return
  locationMentionLoadScheduled = true

  queueMicrotask(async () => {
    locationMentionLoadScheduled = false
    const ids = [...queuedLocationMentionLoads]
    queuedLocationMentionLoads.clear()
    const controller = locationMentionLoadController
    locationMentionLoadController = undefined

    if (!controller || ids.length === 0) return

    try {
      const locations = await controller.getList({
        query: new ODataQueryBuilder().in("id", ids)
      })
      const foundIds = new Set(locations.map((location) => location.id).filter(Boolean))

      // A deleted or otherwise unavailable location can be absent from the list response.
      // Retain the existing detail-request behavior only for those exceptional IDs.
      await Promise.all(
        ids
          .filter((id) => !foundIds.has(id))
          .map(async (id) => {
            try {
              await controller.detail(id)
            } catch {
              /* unresolved labels fall back to a placeholder */
            }
          })
      )
      ids.forEach((id) => loadedLocationMentionIds.add(id))
    } catch {
      // Preserve the original behavior if batching itself is unavailable.
      await Promise.all(
        ids.map(async (id) => {
          try {
            await controller.detail(id)
            loadedLocationMentionIds.add(id)
          } catch {
            /* unresolved labels fall back to a placeholder */
          }
        })
      )
    } finally {
      ids.forEach((id) => {
        locationMentionLoadPromises.delete(id)
        locationMentionLoadResolvers.get(id)?.()
        locationMentionLoadResolvers.delete(id)
      })
    }
  })
}

function loadLocationMention(id: string, controller: LocationController): Promise<void> {
  const existingLoad = locationMentionLoadPromises.get(id)
  if (existingLoad) return existingLoad

  let resolveLoad!: () => void
  const load = new Promise<void>((resolve) => {
    resolveLoad = resolve
  })
  locationMentionLoadPromises.set(id, load)
  locationMentionLoadResolvers.set(id, resolveLoad)
  queuedLocationMentionLoads.add(id)
  locationMentionLoadController ??= controller
  scheduleLocationMentionLoads()

  return load
}

export function isSlashTriggerKey(key: string, keysBefore: string[]): boolean {
  if (key !== LOCATION_TRIGGER) return false
  if (keysBefore.length === 0) return true
  return /\s/.test(keysBefore[keysBefore.length - 1] ?? "")
}

/** `command`: a location search typed as `@locatie <query>`, which stays in the text. */
export type LocationTrigger = "slash" | "double-space" | "command"

export type KeypressMentionAction = { type: "open"; trigger: LocationTrigger } | { type: "close" }

export function getKeypressMentionAction(
  key: string,
  keysBefore: string[],
  isOpen: boolean
): KeypressMentionAction | null {
  if (key !== " ") {
    if (isSlashTriggerKey(key, keysBefore)) return { type: "open", trigger: "slash" }
    return null
  }

  if (!isDoubleSpaceGesture(pushRecentKey(keysBefore, key))) return null
  if (isOpen) return { type: "close" }
  return { type: "open", trigger: "double-space" }
}

// --- Active mention session detection ---

export type MentionSessionCommand = {
  start: number
  query: string
}

export type PersonMentionCommand = MentionSessionCommand

const TRIGGER_SPACES = new Set([" ", " "])
const FILE_TRIGGER = "+"

function isSpaceChar(value: string | undefined): boolean {
  return value !== undefined && TRIGGER_SPACES.has(value)
}

/** A mention query is valid while it does not start with whitespace or contain newlines. */
export function isValidMentionQuery(query: string): boolean {
  return query.length === 0 || !/^\s|[\r\n]/.test(query)
}

function isTriggerBoundary(value: string, index: number): boolean {
  return index === 0 || /\s/.test(value[index - 1])
}

/** Query text for an active location session started at `start`. */
export function getLocationSessionQuery(value: string, cursor: number, start: number): string {
  return value.slice(start + 1, cursor)
}

/** Where the query of an `@locatie <query>` session at `start` begins. */
export function getLocationCommandQueryStart(value: string, start: number): number {
  const command = /^locatie\s*/i.exec(value.slice(start + 1))
  return start + 1 + (command?.[0].length ?? 0)
}

/** Active `/query` mention session before the cursor. */
export function getActiveLocationMentionCommand(before: string): PersonMentionCommand | null {
  let searchEnd = before.length
  while (searchEnd > 0) {
    const slash = before.lastIndexOf(LOCATION_TRIGGER, searchEnd - 1)
    if (slash === -1) return null
    if (!isTriggerBoundary(before, slash)) {
      searchEnd = slash
      continue
    }

    const query = before.slice(slash + 1)
    if (!isValidMentionQuery(query)) return null

    return { start: slash, query }
  }
  return null
}

function isPersonMentionTrigger(value: string, index: number): boolean {
  // Require start of text or whitespace before @ so `email@test` does not open person search.
  return index === 0 || /\s/.test(value[index - 1] ?? "")
}

/** Active `@query` mention session before the cursor. */
export function getActivePersonMentionCommand(before: string): PersonMentionCommand | null {
  let searchEnd = before.length
  while (searchEnd > 0) {
    const at = before.lastIndexOf("@", searchEnd - 1)
    if (at === -1) return null
    if (!isPersonMentionTrigger(before, at)) {
      searchEnd = at
      continue
    }

    const query = before.slice(at + 1)
    if (!isValidMentionQuery(query)) return null

    return { start: at, query }
  }
  return null
}

/** True while the `@` trigger at `start` is still present before the cursor. */
export function isActivePersonMentionSession(
  displayText: string,
  cursor: number,
  start: number
): boolean {
  if (displayText[start] !== "@") return false
  if (!isPersonMentionTrigger(displayText, start)) return false

  const command = getActivePersonMentionCommand(displayText.slice(0, cursor))
  return command?.start === start
}

/** True while the location trigger at `start` is still present before the cursor. */
export function isActiveLocationMentionSession(
  displayText: string,
  cursor: number,
  start: number,
  trigger: LocationTrigger
): boolean {
  if (trigger === "slash") {
    if (displayText[start] !== LOCATION_TRIGGER) return false
    if (!isTriggerBoundary(displayText, start)) return false

    const command = getActiveLocationMentionCommand(displayText.slice(0, cursor))
    return command?.start === start
  }

  if (displayText[start] !== LOCATION_TRIGGER) return false
  if (start <= 0 || displayText[start - 1] !== " ") return false

  return isValidMentionQuery(getLocationSessionQuery(displayText, cursor, start))
}

// --- Mention kinds ---

export type MentionKind = "location" | "file" | "person" | "project" | "business"

/** Per-kind DOM dataset key, test id and label fallback ("no data" placeholder). */
const KIND_META = {
  location: {
    data: "locationId",
    testId: "location-mention",
    fallback: "locatie",
    title: "Locatie"
  },
  file: { data: "fileId", testId: "file-mention", fallback: "bestand", title: "Bestand" },
  person: { data: "personId", testId: "person-mention", fallback: "persoon", title: "Persoon" },
  project: { data: "projectId", testId: "project-mention", fallback: "project", title: "Project" },
  business: {
    data: "businessId",
    testId: "business-mention",
    fallback: "bedrijf",
    title: "Bedrijf"
  }
} as const

const MENTION_KINDS = Object.keys(KIND_META) as MentionKind[]

export function mentionTestId(kind: MentionKind): string {
  return KIND_META[kind].testId
}

const MENTION_ELEMENT_SELECTOR = MENTION_KINDS.map(
  (kind) => `[data-${KIND_META[kind].data.replace(/Id$/, "-id")}]`
).join(",")

export function isMentionEditorEmpty(editor: HTMLElement): boolean {
  return editor.textContent === "" && !editor.querySelector(MENTION_ELEMENT_SELECTOR)
}

// --- Pending location mention UI ---

export const LOCATION_MENTION_HOVER_TITLE = LOCATION_POINTER_HOVER_TITLE

// --- @ mention autocomplete ---

const AT_MENTION_AUTOCOMPLETE_MIN_CHARS = 1
const AT_MENTION_AUTOCOMPLETE_LIMIT = 10

const PROJECT_MENTION_PREFIX = "project"
const BUSINESS_MENTION_PREFIX = "bedrijf"
const RENTAL_MENTION_PREFIX = "retour"
/** Searches persons only; a bare `@` searches persons, projects and businesses together. */
const PERSON_MENTION_PREFIX = "persoon"
const LOCATION_MENTION_PREFIX = "locatie"

/** `rental` searches outstanding rental intakes and inserts plain text instead of a mention token. */
/** `location` (`@locatie`) only hands over to the location picker, like typing `/`. */
export type AtMentionMode = "contact" | "project" | "business" | "rental" | "location"
type CreatableAtMentionMode = Exclude<AtMentionMode, "rental" | "location">

export type ParsedAtMentionQuery = {
  mode: AtMentionMode
  search: string
  /** Whether the query starts with a command (`@project`, `@persoon`, ...) rather than a search. */
  hasPrefix: boolean
}

export type AtMentionSuggestion = {
  key: string
  label: string
  mode: AtMentionMode
  /** Plain text inserted instead of a `{{kind:id}}` token (rental suggestions). */
  insertText?: string
  /** Outstanding rental intake of a rental suggestion, shown like in the processing combobox. */
  rentalIntake?: RentalIntake
  /** `@` command (e.g. `project`) a command suggestion types for the user, see {@link searchAtMentionCommands}. */
  command?: string
  /** Explanation shown next to a command suggestion. */
  description?: string
  /** The "+ Nieuw …" row offered when a command finds nothing (see orCreateSuggestion). */
  isCreateOption?: boolean
  /** Create variant of a command suggestion (e.g. `project+`), offered on the same row. */
  createCommand?: string
  createDescription?: string
}

const AT_MENTION_PREFIX_MODES: [prefix: string, mode: AtMentionMode][] = [
  [PROJECT_MENTION_PREFIX, "project"],
  [BUSINESS_MENTION_PREFIX, "business"],
  [PERSON_MENTION_PREFIX, "contact"],
  [LOCATION_MENTION_PREFIX, "location"],
  [RENTAL_MENTION_PREFIX, "rental"]
]

export function parseAtMentionQuery(query: string): ParsedAtMentionQuery {
  const lower = query.toLowerCase()
  for (const [prefix, mode] of AT_MENTION_PREFIX_MODES) {
    // Only as a whole word: `@projecten` searches for "projecten", not `@project` "en".
    if (lower.startsWith(prefix) && /^($|\s|\+)/.test(lower.slice(prefix.length))) {
      return { mode, search: query.slice(prefix.length).trimStart(), hasPrefix: true }
    }
  }
  return { mode: "contact", search: query, hasPrefix: false }
}

const PROJECT_MENTION_SEARCH_FIELDS = [
  { name: "name" },
  { name: "executor.knownBusinessEntity.name" }
] as const

/** Label of a person tag: the person's name, then their company, e.g. "Jan Jansen, Bouw BV". */
export function contactMentionLabel(contact: KnownBusinessEntityContact): string {
  const name = contact.person?.fullName || "Onbekend"
  // Undefined while the company is still a lazy id or has no name.
  const company = (contact.knownBusinessEntityName as string | undefined)?.trim()
  return company ? `${name}, ${company}` : name
}

function projectToMentionSuggestion(project: Project): AtMentionSuggestion | null {
  return project.id ? { key: project.id, label: project.getMentionLabel(), mode: "project" } : null
}

const CREATE_MENTION_TRIGGER = "+"

/**
 * Detects a create intent from an `@` query. A trailing single `+` right after
 * the mode prefix (`@persoon+`, `@project+`, `@bedrijf+`) requests creating a new entity
 * of that mode. Returns `null` when the query is a regular search.
 */
export function parseAtMentionCreateIntent(query: string): CreatableAtMentionMode | null {
  if (!query.endsWith(CREATE_MENTION_TRIGGER)) return null
  const parsed = parseAtMentionQuery(query.slice(0, -CREATE_MENTION_TRIGGER.length))
  // Only treat as a create intent when the `+` immediately follows the prefix,
  // i.e. there is no additional search text before it (`@project foo+` is a search).
  if (!parsed.hasPrefix || parsed.search.length > 0) return null
  // Rental intakes are booked on an instruction and locations are found with the location picker.
  if (parsed.mode === "rental" || parsed.mode === "location") return null
  return parsed.mode
}

/** The `@` commands, listed when typing `@` (see {@link searchAtMentionCommands}). */
const AT_MENTION_COMMANDS: {
  command: string
  mode: AtMentionMode
  description: string
  /** Description of the command's create variant (`@<command>+`), if it has one. */
  createDescription?: string
}[] = [
  { command: LOCATION_MENTION_PREFIX, mode: "location", description: "Locatie taggen" },
  { command: RENTAL_MENTION_PREFIX, mode: "rental", description: "Uitstaande huuritems taggen" },
  {
    command: PROJECT_MENTION_PREFIX,
    mode: "project",
    description: "Project taggen",
    createDescription: "Nieuw project aanmaken"
  },
  {
    command: BUSINESS_MENTION_PREFIX,
    mode: "business",
    description: "Bedrijf taggen",
    createDescription: "Nieuw bedrijf aanmaken"
  },
  {
    command: PERSON_MENTION_PREFIX,
    mode: "contact",
    description: "Persoon taggen",
    createDescription: "Nieuwe persoon aanmaken"
  }
]

/** Placeholder line listing the `@` commands: "Typ @ voor @locatie, … of @persoon (@persoon+ voor nieuw)". */
export const AT_MENTION_PLACEHOLDER_HINT = (() => {
  const commands = AT_MENTION_COMMANDS.map(({ command }) => `@${command}`)
  const lastCommand = commands.at(-1)
  return `Typ @ voor ${commands.slice(0, -1).join(", ")} of ${lastCommand} (${lastCommand}${CREATE_MENTION_TRIGGER} voor nieuw)`
})()

/** The `@` commands starting with what was typed after `@`, e.g. all of them for a bare `@`. */
export function searchAtMentionCommands(query: string): AtMentionSuggestion[] {
  const typed = query.trim().toLowerCase()
  return AT_MENTION_COMMANDS.filter(({ command }) => command.startsWith(typed)).map(
    ({ command, mode, description, createDescription }) => ({
      key: `command-${command}`,
      label: `@${command}`,
      mode,
      command,
      description,
      ...(createDescription
        ? { createCommand: `${command}${CREATE_MENTION_TRIGGER}`, createDescription }
        : {})
    })
  )
}

/** Fuzzy-search one suggestion pool (query hit) or list it alphabetically (empty query). */
function searchSuggestionPool<T>(
  query: string,
  pool: T[],
  fields: { name: string }[],
  toSuggestion: (item: T) => AtMentionSuggestion | null,
  limit = AT_MENTION_AUTOCOMPLETE_LIMIT
): AtMentionSuggestion[] {
  const q = query.trim()
  const matches = q
    ? fuzzySearchCollection(pool, fields, q)
    : [...pool].sort((a, b) =>
        (toSuggestion(a)?.label ?? "").localeCompare(toSuggestion(b)?.label ?? "")
      )
  return matches
    .map(toSuggestion)
    .filter((s): s is AtMentionSuggestion => !!s?.key)
    .slice(0, limit)
}

export function searchAtMentionSuggestions(
  query: string,
  contacts: KnownBusinessEntityContact[],
  projects: Project[],
  businesses: KnownBusinessEntity[],
  limit = AT_MENTION_AUTOCOMPLETE_LIMIT
): AtMentionSuggestion[] {
  const parsed = parseAtMentionQuery(query)
  // `@locatie` opens the location picker instead.
  if (parsed.mode === "location") return []
  if (parsed.mode === "project") {
    const projectSuggestions = searchSuggestionPool(
      parsed.search,
      projects,
      [...PROJECT_MENTION_SEARCH_FIELDS],
      projectToMentionSuggestion,
      limit
    )
    return orCreateSuggestion(projectSuggestions, "project")
  }
  if (parsed.mode === "business") {
    const businessSuggestions = searchSuggestionPool(
      parsed.search,
      businesses,
      [{ name: "name" }],
      (business) =>
        business.id ? { key: business.id, label: business.name, mode: "business" } : null,
      limit
    )
    return orCreateSuggestion(businessSuggestions, "business")
  }

  const contactSuggestions = searchSuggestionPool(
    parsed.search,
    contacts.filter((contact) => contact.isActive),
    [
      { name: "person.firstName" },
      { name: "person.lastName" },
      { name: "knownBusinessEntity.name" }
    ],
    (contact) =>
      contact.id ? { key: contact.id, label: contactMentionLabel(contact), mode: "contact" } : null,
    limit
  )
  // `@persoon` searches persons only.
  if (parsed.hasPrefix) return orCreateSuggestion(contactSuggestions, "contact")

  const projectSuggestions = searchSuggestionPool(
    parsed.search,
    projects,
    [...PROJECT_MENTION_SEARCH_FIELDS],
    projectToMentionSuggestion,
    limit
  )
  const businessSuggestions = searchSuggestionPool(
    parsed.search,
    businesses,
    [{ name: "name" }],
    (business) =>
      business.id ? { key: business.id, label: business.name, mode: "business" } : null,
    limit
  )

  return [...contactSuggestions, ...projectSuggestions, ...businessSuggestions]
    .sort((a, b) => a.label.localeCompare(b.label))
    .slice(0, limit)
}

const CREATE_SUGGESTIONS: Record<CreatableAtMentionMode, { prefix: string; label: string }> = {
  project: { prefix: PROJECT_MENTION_PREFIX, label: "Nieuw project" },
  business: { prefix: BUSINESS_MENTION_PREFIX, label: "Nieuw bedrijf" },
  contact: { prefix: PERSON_MENTION_PREFIX, label: "Nieuw persoon" }
}

/**
 * `@project`, `@bedrijf` and `@persoon` without matches offer to add a new one instead, which
 * opens the same form as typing `@project+` and the like.
 */
function orCreateSuggestion(
  suggestions: AtMentionSuggestion[],
  mode: CreatableAtMentionMode
): AtMentionSuggestion[] {
  if (suggestions.length > 0) return suggestions
  const { prefix, label } = CREATE_SUGGESTIONS[mode]
  return [
    {
      key: `create-${mode}`,
      label,
      mode,
      command: `${prefix}${CREATE_MENTION_TRIGGER}`,
      isCreateOption: true
    }
  ]
}

/** Suggestions per page of an `@` list; scrolling to the end of the list shows the next page. */
export function atMentionPageSize(mode: AtMentionMode): number {
  return mode === "rental" ? RENTAL_MENTION_PAGE_SIZE : AT_MENTION_AUTOCOMPLETE_LIMIT
}

// --- @retour rental intake autocomplete ---

/** Rental suggestions shown per page; scrolling to the end of the list shows the next page. */
export const RENTAL_MENTION_PAGE_SIZE = 25

const PROJECT_MENTION_TOKEN_RE = /\{\{project:([0-9a-fA-F-]{8,})(?::[^{}\n]*)?\}\}/g

/**
 * The project tagged with `@project` closest before `bodyOffset`, falling back to the last
 * project tag in the body. `@retour` ranks that project's returns first.
 */
export function findTaggedProjectId(body: string, bodyOffset = body.length): string | null {
  let lastBefore: string | null = null
  let last: string | null = null
  for (const match of body.matchAll(PROJECT_MENTION_TOKEN_RE)) {
    if ((match.index ?? 0) < bodyOffset) lastBefore = match[1]
    last = match[1]
  }
  return lastBefore ?? last
}

/** Project of a rental intake issuance; `project` stays a UUID until the model manager resolves it. */
function rentalIntakeProject(
  rentalIntake: RentalIntake,
  resolveProject: (id: string) => Project | null
): Project | null {
  if (isPopulated(rentalIntake.project)) return rentalIntake.project
  const projectId = lazyFieldId(rentalIntake.project)
  return projectId ? resolveProject(projectId) : null
}

/** Text inserted for an outstanding rental intake, e.g. "5 stuks Steigerbuis 6m". */
export function rentalIntakeMentionText(rentalIntake: RentalIntake): string {
  const item = rentalIntake.rentalIntakeItem
  const quantity = normalizeDecimal(rentalIntake.getRemainingQuantity(), { minDecimalPlaces: 0 })
  return [quantity, item.getUnitName(), item.name].filter(Boolean).join(" ")
}

/**
 * Search text of a return: the fields of the processing combobox's return search text, with the
 * resolved project, since the intake's own `project` (and its executor) may still be an id.
 */
function rentalReturnSearchText(rentalIntake: RentalIntake, project: Project | null): string {
  const executor = project && isPopulated(project.executor) ? project.executor : null
  return [
    rentalIntake.rentalIntakeItem.getDisplayText(),
    unitToAlternativeName(rentalIntake.rentalIntakeItem.unit),
    rentalIntake.associatedRentalDispense?.rentalDispenseItem?.getDisplayText(),
    getProjectDisplayName(project),
    executor && isPopulated(executor.person) ? executor.person.fullName : undefined,
    executor?.knownBusinessEntityName
  ]
    .filter(Boolean)
    .join(" ")
}

/** Text inserted for a picked return, e.g. "retour 5 stuks Steigerbuis 6m van project Noord". */
export function rentalReturnMentionText(
  rentalIntake: RentalIntake,
  project: Project | null
): string {
  const returnText = `retour ${rentalIntakeMentionText(rentalIntake)}`
  return project ? `${returnText} van project ${getProjectDisplayName(project)}` : returnText
}

function rentalIntakeToMentionSuggestion(
  rentalIntake: RentalIntake,
  project: Project | null
): AtMentionSuggestion | null {
  if (!rentalIntake.id) return null
  return {
    key: rentalIntake.id,
    label: rentalIntakeMentionText(rentalIntake),
    mode: "rental",
    insertText: rentalReturnMentionText(rentalIntake, project),
    rentalIntake
  }
}

/**
 * Outstanding (not yet fully returned) rental intake issuances matching `query`, searched and
 * ranked like the return candidates of the processing combobox, with the tagged project in the
 * place of the instruction's project: that project first, then projects with the same executor,
 * then those of the same known business entity, then the others by name.
 */
export function searchRentalIntakeMentionSuggestions(
  query: string,
  rentalIntakes: RentalIntake[],
  taggedProject: Project | null,
  resolveProject: (id: string) => Project | null,
  limit = RENTAL_MENTION_PAGE_SIZE
): AtMentionSuggestion[] {
  const candidates = rentalIntakes.flatMap((rentalIntake) => {
    // Like the processing combobox, only intake and dispense pairs can be returned.
    if (!rentalIntake.associatedRentalDispense) return []
    if (!rentalIntake.isIssuance() || rentalIntake.getRemainingQuantity() <= 0) return []
    return [{ rentalIntake, project: rentalIntakeProject(rentalIntake, resolveProject) }]
  })
  const q = query.trim()
  // Without a query, list by product (labels start with the quantity), most recent issuance first.
  const matches = q
    ? fuzzySearchCollection(
        candidates,
        [
          {
            name: "SearchText",
            getFn: ({ rentalIntake, project }) => rentalReturnSearchText(rentalIntake, project)
          }
        ],
        q
      )
    : candidates.sort(
        (a, b) =>
          a.rentalIntake.rentalIntakeItem.name.localeCompare(
            b.rentalIntake.rentalIntakeItem.name
          ) ||
          (b.rentalIntake.commencementDate?.valueOf() ?? 0) -
            (a.rentalIntake.commencementDate?.valueOf() ?? 0)
      )
  // Stable sort: keeps the order above within each project.
  return matches
    .sort((a, b) => compareProjectsByRelation(a.project, b.project, taggedProject))
    .map(({ rentalIntake, project }) => rentalIntakeToMentionSuggestion(rentalIntake, project))
    .filter((s): s is AtMentionSuggestion => !!s?.key)
    .slice(0, limit)
}

// --- Core mention logic ---

type BodySegment =
  | { type: "text"; value: string }
  | { type: "mention"; kind: MentionKind; id: string; label?: string }

/** `{{kind:id}}`, or `{{kind:id:label}}` when the token stores its label (person tags). */
const MENTION_TOKEN_RE =
  /\{\{(location|file|person|project|business):((?:pending-location-[0-9a-fA-F-]+)|(?:[0-9a-fA-F-]{8,}))(?::([^{}\n]*))?\}\}/g

/** Resolve a mention's label; `storedLabel` is the label stored in its token, if any. */
type MentionLabelFor = (kind: MentionKind, id: string, storedLabel?: string) => string

/**
 * Body token for a mention. Person tokens also store the person's name, so readers that cannot
 * load the contact (e.g. service providers) still see who is meant.
 */
export function formatMentionToken(kind: MentionKind, id: string, label?: string): string {
  const storedLabel = kind === "person" ? label?.replace(/[{}\r\n]/g, "").trim() : undefined
  return storedLabel ? `{{${kind}:${id}:${storedLabel}}}` : `{{${kind}:${id}}}`
}

/** Client-side location IDs awaiting server persistence after optimistic insert. */
const pendingLocationIds = new Set<string>()
const pendingLocationLabels = new Map<string, string>()

export function isPendingLocationId(id: string): boolean {
  return pendingLocationIds.has(id)
}

export function getPendingLocationBaseLabel(id: string): string {
  return pendingLocationLabels.get(id) ?? "locatie"
}

function clearPendingLocation(id: string) {
  pendingLocationIds.delete(id)
  pendingLocationLabels.delete(id)
}

let trackingMapVisible = false

/** Set by {@link SupplierAndUserTrackingMap} when the Kaart column is visible in the viewport. */
export function setTrackingMapVisible(value: boolean) {
  trackingMapVisible = value
}

function mentionLinkClass(kind: MentionKind, interactive = true): string {
  const base =
    kind === "location"
      ? "font-bold underline underline-offset-2"
      : kind === "person" || kind === "project" || kind === "business"
        ? "font-bold text-primary dark:text-primary-dark underline underline-offset-2"
        : "font-bold text-info underline decoration-solid underline-offset-2"
  if (!interactive) {
    if (kind === "person" || kind === "project" || kind === "business")
      return "font-bold text-primary dark:text-primary-dark"
    if (kind === "location") return "font-bold"
    return "font-bold text-info dark:text-info-dark"
  }
  if (kind === "file") return `${base} cursor-pointer hover:text-info/80`
  return `${base} cursor-pointer`
}

export function getFileTriggerAt(value: string, cursor: number): number | null {
  const i = cursor - 1
  if (i < 1 || value[i] !== FILE_TRIGGER || value[i - 1] !== FILE_TRIGGER) return null
  if (!isTriggerBoundary(value, i - 1)) return null
  return i - 1
}

function parseBody(body: string): BodySegment[] {
  const segments: BodySegment[] = []
  let last = 0
  let match: RegExpExecArray | null
  MENTION_TOKEN_RE.lastIndex = 0
  while ((match = MENTION_TOKEN_RE.exec(body)) !== null) {
    if (match.index > last) segments.push({ type: "text", value: body.slice(last, match.index) })
    segments.push({ type: "mention", kind: match[1] as MentionKind, id: match[2], label: match[3] })
    last = match.index + match[0].length
  }
  if (last < body.length) segments.push({ type: "text", value: body.slice(last) })
  return segments
}

export function formatMentionText(body: string, labelFor: MentionLabelFor): string {
  return parseBody(body)
    .map((segment) =>
      segment.type === "mention" ? labelFor(segment.kind, segment.id, segment.label) : segment.value
    )
    .join("")
}

/** Keep stored tokens separate from the plain labels edited in a native textarea. */
export class MentionTextareaDocument {
  display = ""
  private mentions: { start: number; end: number; token: string }[] = []
  private history: { display: string; body: string }[] = []

  constructor(private labelFor: MentionLabelFor) {}

  get body(): string {
    let position = 0
    let body = ""
    for (const mention of this.mentions) {
      body += this.display.slice(position, mention.start) + mention.token
      position = mention.end
    }
    return body + this.display.slice(position)
  }

  get displaySegments(): Array<BodySegment & { start?: number; end?: number }> {
    const segments: Array<BodySegment & { start?: number; end?: number }> = []
    let position = 0
    for (const mention of this.mentions) {
      if (mention.start > position)
        segments.push({ type: "text", value: this.display.slice(position, mention.start) })
      const segment = parseBody(mention.token)[0]
      if (segment?.type === "mention")
        segments.push({
          ...segment,
          label: this.display.slice(mention.start, mention.end),
          start: mention.start,
          end: mention.end
        })
      position = mention.end
    }
    if (position < this.display.length)
      segments.push({ type: "text", value: this.display.slice(position) })
    return segments
  }

  setBody(body: string) {
    this.display = ""
    this.mentions = []
    for (const segment of parseBody(body)) {
      if (segment.type === "text") {
        this.display += segment.value
        continue
      }
      const label = this.labelFor(segment.kind, segment.id, segment.label)
      const start = this.display.length
      this.display += label
      this.mentions.push({
        start,
        end: this.display.length,
        token: formatMentionToken(
          segment.kind,
          segment.id,
          segment.kind === "person" && label !== KIND_META.person.fallback ? label : segment.label
        )
      })
    }
    this.remember()
  }

  private remember() {
    const body = this.body
    if (this.history.at(-1)?.body !== body) {
      this.history.push({ display: this.display, body })
      if (this.history.length > 100) this.history.shift()
    }
  }

  /** Editing a label turns that mention into ordinary text; untouched mentions retain their IDs. */
  edit(value: string, selection?: { start: number; end: number }, inputType?: string) {
    if (value === this.display) return
    if (inputType === "historyUndo" || inputType === "historyRedo") {
      const previous = [...this.history].reverse().find((state) => state.display === value)
      if (previous) {
        this.setBody(previous.body)
        return
      }
    }
    const old = this.display
    let start = selection?.start ?? 0
    let end = selection?.end ?? 0
    if (selection && start === end && value.length < old.length) {
      const deleted = old.length - value.length
      if (inputType?.endsWith("Backward")) start = Math.max(0, start - deleted)
      else if (inputType?.endsWith("Forward")) end = Math.min(old.length, end + deleted)
    }
    const insertedLength = value.length - old.length + end - start
    if (
      !selection ||
      insertedLength < 0 ||
      value.slice(0, start) !== old.slice(0, start) ||
      value.slice(start + insertedLength) !== old.slice(end)
    ) {
      start = 0
      while (start < Math.min(old.length, value.length) && old[start] === value[start]) start++
      end = old.length
      let nextEnd = value.length
      while (end > start && nextEnd > start && old[end - 1] === value[nextEnd - 1]) {
        end--
        nextEnd--
      }
    }
    const delta = value.length - old.length
    this.mentions = this.mentions.flatMap((mention) => {
      if (mention.end <= start) return [mention]
      if (mention.start >= end)
        return [{ ...mention, start: mention.start + delta, end: mention.end + delta }]
      return []
    })
    this.display = value
    this.remember()
  }

  bodyOffset(offset: number): number {
    let delta = 0
    for (const mention of this.mentions) {
      if (offset <= mention.start) break
      if (offset < mention.end) return mention.start + delta
      delta += mention.token.length - (mention.end - mention.start)
    }
    return offset + delta
  }

  mentionAt(offset: number) {
    const range =
      this.mentions.find((mention) => offset >= mention.start && offset < mention.end) ??
      this.mentions.find((mention) => offset === mention.end)
    if (!range) return null
    const segment = parseBody(range.token)[0]
    return segment?.type === "mention" ? { ...segment, start: range.start, end: range.end } : null
  }

  replace(start: number, end: number, replacement: string) {
    // Clear a partially edited label before converting display offsets to body offsets.
    this.mentions = this.mentions.filter(
      (mention) =>
        !(start > mention.start && start < mention.end) &&
        !(end > mention.start && end < mention.end)
    )
    const body = this.body
    this.setBody(
      body.slice(0, this.bodyOffset(start)) + replacement + body.slice(this.bodyOffset(end))
    )
  }
}

/** A resolvable mention entity (everything except files). */
type MentionEntityKind = Exclude<MentionKind, "file">
type MentionEntity = Location | KnownBusinessEntityContact | Project | KnownBusinessEntity

export function formatLocationMentionLabel(
  location: { name?: string | null; address?: string | null } | null | undefined,
  options?: { preferAddress?: boolean; nameWithAddress?: boolean }
): string {
  const name = location?.name?.trim()
  const address = location?.address?.trim()

  if (options?.nameWithAddress) {
    if (name && address) return stripCountryFromLocationText(`${name}: ${address}`)
    const fallback = address ?? name ?? "locatie"
    return fallback === "locatie" ? fallback : stripCountryFromLocationText(fallback)
  }

  const raw = options?.preferAddress ? (address ?? name ?? "locatie") : name || address || "locatie"
  return raw === "locatie" ? raw : stripCountryFromLocationText(raw)
}

/**
 * Shared inline-mention logic (PNTS-2917): resolve `{{location:<uuid>}}` and
 * `{{file:<uuid>}}` references — a location's live name + map modal, and a
 * file's type-aware label + viewer.
 */
/**
 * One revision counter per location store, bumped by a single deep watcher on the tenant base
 * locations. Every MentionText used to create its own deep watcher, and a deep watcher
 * traverses the whole collection when it is created: with a few thousand locations and a card
 * per instruction, that traversal ran again for every card mounted while scrolling.
 */
const tenantBaseLocationsRevisions = new WeakMap<object, Ref<number>>()

function useTenantBaseLocationsRevision(): Ref<number> {
  const store = useLocationStore()
  let revision = tenantBaseLocationsRevisions.get(store)
  if (!revision) {
    const counter = ref(0)
    const { tenantBaseLocations } = storeToRefs(store)
    // Detached scope: the watcher outlives the component that first asked for it.
    effectScope(true).run(() => {
      watch(tenantBaseLocations, () => counter.value++, { deep: true })
    })
    tenantBaseLocationsRevisions.set(store, counter)
    revision = counter
  }
  return revision
}

export function useMention(options?: {
  preferLocationAddress?: boolean
  /** Show `locatienaam: adres` when both are available (e.g. instruction details). */
  locationNameWithAddress?: boolean
  /** Read-only location sheet (e.g. app instruction details). */
  useLocationDetailModal?: boolean
}) {
  const controller = inject<LocationController>("locationController")
  const { tenantBaseLocations } = storeToRefs(useLocationStore())
  const { activeKnownBusinessEntitiesContacts, knownBusinessEntitiesContacts } = storeToRefs(
    useKnownBusinessEntitiesContactsStore()
  )
  const { projects } = storeToRefs(useProjectStore())
  const { knownBusinessEntities } = storeToRefs(useKnownBusinessEntityStore())
  const preferLocationAddress = options?.preferLocationAddress ?? false
  const locationNameWithAddress = options?.locationNameWithAddress ?? false

  const locationLabelsRevision = useTenantBaseLocationsRevision()

  const mapLocation = ref<Location | null>(null)
  const mapAnchor = ref<{ x: number; y: number } | null>(null)
  const { activeUser } = storeToRefs(useAuthStore())
  const modelFormStore = useModelFormStore()

  function shouldUseLocationDetailModal(): boolean {
    if (options?.useLocationDetailModal) return true
    return shouldUseReadOnlyMentionDetailModal(activeUser.value?.knownBusinessEntityContact)
  }

  function useReadOnlyMentionDetailModal(): boolean {
    return shouldUseReadOnlyMentionDetailModal(activeUser.value?.knownBusinessEntityContact)
  }

  function usesInlineLocationMap(): boolean {
    if (trackingMapVisible) return false
    return !shouldUseLocationDetailModal()
  }

  const managed = <T>(id: string) => ModelManager.getInstance().get(id) as T | undefined

  function getContact(id: string): KnownBusinessEntityContact | null {
    return (
      managed<KnownBusinessEntityContact>(id) ??
      knownBusinessEntitiesContacts.value.find((contact) => contact.id === id) ??
      activeKnownBusinessEntitiesContacts.value.find((contact) => contact.id === id) ??
      knownBusinessEntitiesContacts.value.find((contact) => contact.person?.id === id) ??
      activeKnownBusinessEntitiesContacts.value.find((contact) => contact.person?.id === id) ??
      null
    )
  }

  /** Resolve a mention's entity from the model manager or the matching store. */
  function resolveEntity(kind: MentionEntityKind, id: string): MentionEntity | null {
    if (kind === "person") return getContact(id)
    if (kind === "project")
      return managed<Project>(id) ?? projects.value.find((p) => p.id === id) ?? null
    if (kind === "business")
      return (
        managed<KnownBusinessEntity>(id) ??
        knownBusinessEntities.value.find((b) => b.id === id) ??
        null
      )
    return managed<Location>(id) ?? tenantBaseLocations.value.find((l) => l.id === id) ?? null
  }

  function locationLabel(id: string): string {
    if (isPendingLocationId(id)) {
      return stripCountryFromLocationText(getPendingLocationBaseLabel(id))
    }
    const location = resolveEntity("location", id) as Location | null
    return formatLocationMentionLabel(location, {
      preferAddress: preferLocationAddress,
      nameWithAddress: locationNameWithAddress
    })
  }

  function fileLabel(id: string): string {
    const file = managed<FileRelation>(id)?.file
    if (!file) return KIND_META.file.fallback
    if (file.isImage()) return "afbeelding"
    if (file.extension?.toLowerCase() === "pdf") return "pdf"
    if (file.isEmail()) return "e-mail"
    if (file.isDocument()) return "document"
    return KIND_META.file.fallback
  }

  /** The live label of a mention, else the label stored in its token, else the kind. */
  function labelFor(kind: MentionKind, id: string, storedLabel?: string): string {
    if (kind === "file") return fileLabel(id)
    if (kind === "location") return locationLabel(id)
    const entity = resolveEntity(kind, id)
    const name =
      kind === "person"
        ? entity && contactMentionLabel(entity as KnownBusinessEntityContact)
        : kind === "project"
          ? (entity as Project | null)?.getMentionLabel().trim()
          : (entity as KnownBusinessEntity | null)?.name?.trim()
    return name || storedLabel || KIND_META[kind].fallback
  }

  /** Hover title for a rendered mention. */
  function mentionTitle(kind: MentionKind, id: string, storedLabel?: string): string {
    if (kind === "file") return `Open ${fileLabel(id)}`
    if (kind === "location") return LOCATION_MENTION_HOVER_TITLE
    // A stored label alone cannot be opened (the entity is not available to this reader).
    if (!resolveEntity(kind, id)) return storedLabel || KIND_META[kind].title
    return `Open ${labelFor(kind, id)}`
  }

  async function ensureLoaded(id: string): Promise<void> {
    if (isPendingLocationId(id)) return
    const location = resolveEntity("location", id) as Location | null
    const needsFetch =
      !location || location.name == null || location.latitude == null || location.longitude == null
    if (!needsFetch || loadedLocationMentionIds.has(id) || !controller) return
    await loadLocationMention(id, controller)
  }

  async function ensureBodyMentionsLoaded(body: string): Promise<void> {
    const locationIds = parseBody(body).flatMap((segment) =>
      segment.type === "mention" && segment.kind === "location" ? [segment.id] : []
    )
    await Promise.all(locationIds.map(ensureLoaded))
  }

  function formatBodyForDisplay(body: string): string {
    return formatMentionText(body, labelFor)
  }

  function clearLocationMentionFocus(id?: string) {
    Bus.emit("location-mention-focus-clear", { id })
  }

  function emitLocationFocus(location: Location) {
    if (location.id && location.latitude != null && location.longitude != null) {
      Bus.emit("location-mention-focus", {
        id: location.id,
        latitude: location.latitude,
        longitude: location.longitude,
        name: location.name,
        address: location.address
      })
    }
  }

  function openMap(location: Location, event?: Event) {
    emitLocationFocus(location)
    if (trackingMapVisible) return
    mapAnchor.value = event instanceof MouseEvent ? { x: event.clientX, y: event.clientY } : null
    mapLocation.value = location
  }

  async function openMapById(id: string, event?: Event) {
    if (isPendingLocationId(id)) return

    if (mapLocation.value?.id === id) {
      closeMap()
      clearLocationMentionFocus(id)
      return
    }

    if (mentionDetail.value?.kind === "location" && mentionDetail.value.id === id) {
      closeMentionDetail()
      clearLocationMentionFocus(id)
      return
    }

    await ensureLoaded(id)
    const location = resolveEntity("location", id) as Location | null
    if (!location) return
    if (shouldUseLocationDetailModal()) {
      emitLocationFocus(location)
      openMentionDetail({ kind: "location", id })
      return
    }
    openMap(location, event)
  }

  function closeMap() {
    mapLocation.value = null
  }

  async function refreshOpenMapLocation() {
    const id = mapLocation.value?.id
    if (!id || !controller) return
    try {
      const location = await controller.detail(id)
      if (location) mapLocation.value = location
    } catch {
      /* keep showing the last known location */
    }
  }

  function openFile(id: string) {
    const fileRelation = managed<FileRelation>(id)
    if (fileRelation) void FileViewerController.display([fileRelation], fileRelation)
  }

  async function openLocationModalById(id: string, event?: Event) {
    if (isPendingLocationId(id)) return

    await ensureLoaded(id)
    const location = resolveEntity("location", id) as Location | null
    if (!location) return

    if (shouldUseLocationDetailModal()) {
      emitLocationFocus(location)
      openMentionDetail({ kind: "location", id })
      return
    }

    emitLocationFocus(location)
    if (trackingMapVisible) {
      Bus.emit("location-map-modal-open", { location })
      return
    }

    mapAnchor.value = event instanceof MouseEvent ? { x: event.clientX, y: event.clientY } : null
    mapLocation.value = location
  }

  let pendingLocationClick: {
    id: string
    event: Event
    timer: ReturnType<typeof setTimeout>
  } | null = null

  function handleLocationMentionClick(id: string, event: Event) {
    if (pendingLocationClick) clearTimeout(pendingLocationClick.timer)
    pendingLocationClick = {
      id,
      event,
      timer: setTimeout(() => {
        const pending = pendingLocationClick
        pendingLocationClick = null
        if (pending) void openMapById(pending.id, pending.event)
      }, LOCATION_MENTION_CLICK_DELAY_MS)
    }
  }

  function handleLocationMentionDoubleClick(id: string, event: Event) {
    if (pendingLocationClick) {
      clearTimeout(pendingLocationClick.timer)
      pendingLocationClick = null
    }
    event.preventDefault()
    event.stopPropagation()
    void openLocationModalById(id, event)
  }

  /** Open the detail/edit UI for any mention kind. */
  function openMention(kind: MentionKind, id: string, event?: Event) {
    if (kind === "location") return void openMapById(id, event)
    if (kind === "file") return openFile(id)

    const entity = resolveEntity(kind, id)
    if (!entity) return
    if (useReadOnlyMentionDetailModal()) {
      openMentionDetail({ kind, id })
      return
    }
    if (kind === "person") {
      const contact = entity as KnownBusinessEntityContact
      modelFormStore.openForm({
        model: contact.person,
        creationParameters: { focusRoleButton: contact }
      })
    } else {
      modelFormStore.openForm({ model: entity })
    }
  }

  return {
    parseBody,
    mentionLinkClass,
    labelFor,
    mentionTitle,
    resolveEntity,
    ensureLoaded,
    ensureBodyMentionsLoaded,
    formatBodyForDisplay,
    mapLocation,
    mapAnchor,
    openMention,
    openMapById,
    openLocationModalById,
    handleLocationMentionClick,
    handleLocationMentionDoubleClick,
    closeMap,
    refreshOpenMapLocation,
    fileLabel,
    openFile,
    usesInlineLocationMap,
    locationLabelsRevision
  }
}

type PendingAtMentionCreate = {
  offset: number
  mode: CreatableAtMentionMode
  entityId: string
}

type SelectorMode = "location" | "at"

/** Per-@-mode mention kind, create-form model class and saved-model matcher. */
const MODE_META = {
  contact: {
    kind: "person" as const,
    createModel: () => ModelFactory.createModel(Person),
    savedClassName: () => KnownBusinessEntityContact.className,
    fallback: "persoon"
  },
  project: {
    kind: "project" as const,
    createModel: () => ModelFactory.createModel(Project),
    savedClassName: () => Project.className,
    fallback: "project"
  },
  business: {
    kind: "business" as const,
    createModel: () => ModelFactory.createModel(KnownBusinessEntity),
    savedClassName: () => KnownBusinessEntity.className,
    fallback: "bedrijf"
  }
}

/** Native textarea editor with location autocomplete and file mentions. */
export function useMentionEditor(options: {
  text: Ref<string | null | undefined>
  /** Optional explicit Places key for isolated editors, otherwise use the tenant's key. */
  apiKey?: Ref<string | undefined>
  autoFocus?: boolean
  transformClipboard?: (value: string) => string
  onInput: (event: Event) => void
  onFileTrigger: () => void
}) {
  const MIN_CHARS = 3
  const locationController = inject<LocationController>("locationController")
  const { activeUser } = storeToRefs(useAuthStore())
  const { activeKnownBusinessEntitiesContacts, knownBusinessEntitiesContacts } = storeToRefs(
    useKnownBusinessEntitiesContactsStore()
  )
  const { projects } = storeToRefs(useProjectStore())
  const { knownBusinessEntities } = storeToRefs(useKnownBusinessEntityStore())
  const rentalIntakeController = inject<RentalIntakeController>("rentalIntakeController")
  const { returnableRentalIntakes, retrievedSearchStrings } = storeToRefs(useRentalIntakeStore())
  const mention = useMention()
  const modelFormStore = useModelFormStore()

  const rootEl = ref<HTMLElement>()
  const editorEl = ref<HTMLTextAreaElement>()
  const editorText = ref("")
  const mentionDocument = new MentionTextareaDocument(mention.labelFor)
  const requestedLocations = new Set<string>()
  let inputSelection: { start: number; end: number } | undefined
  const isFocused = ref(false)
  const { open, loading, items, highlightedIndex, query, trimmedQuery } = useSuggestionList()
  // Shallow: suggestions are replaced as a whole, and rental ones hold full RentalIntake models.
  const atItems = shallowRef<AtMentionSuggestion[]>([])
  const atMentionMode = ref<AtMentionMode>("contact")
  /** Project tagged with `@project` that an `@retour` session ranks first, if any. */
  const rentalProjectId = ref<string | null>(null)
  const rentalProject = computed(() =>
    rentalProjectId.value ? resolveProject(rentalProjectId.value) : null
  )
  /** Number of `@` suggestions shown; grows a page at a time on scrolling to the end of the list. */
  const atSuggestionLimit = ref(AT_MENTION_AUTOCOMPLETE_LIMIT)
  /** Rental searches whose last server batch was empty: there is nothing left to page in. */
  const exhaustedRentalSearchKeys = new Set<string>()
  const selectorMode = ref<SelectorMode | null>(null)
  const pendingFileOffset = ref<number | null>(null)
  const pendingAtMentionCreate = ref<PendingAtMentionCreate | null>(null)
  const slashStart = ref<number | null>(null)
  const slashEnd = ref<number | null>(null)
  const locationTrigger = ref<LocationTrigger | null>(null)
  const recentKeys = ref<string[]>([])
  const pendingMentionAction = ref<KeypressMentionAction | null>(null)

  const delayedSearch = useDelayedFunction(runSearch, 250)

  onMounted(() => {
    renderEditor(options.text.value ?? "")
    if (options.autoFocus) nextTick(focus)
  })

  watch(options.text, (value) => {
    if (!isFocused.value) renderEditor(value ?? "")
  })
  watch(useTenantBaseLocationsRevision(), () => {
    if (!isFocused.value) {
      renderEditor(options.text.value ?? "")
      return
    }
    refreshLocationMentionLabels()
  })

  function refreshLocationMentionLabels() {
    renderEditor(mentionDocument.body, true)
  }

  function refreshLocationMentionLabel() {
    refreshLocationMentionLabels()
  }

  function walkEditor(format: "body" | "display"): string {
    return format === "body" ? mentionDocument.body : mentionDocument.display
  }

  function ensureLoaded(id: string) {
    if (isPendingLocationId(id) || requestedLocations.has(id)) return
    requestedLocations.add(id)
    void mention.ensureLoaded(id).then(() => refreshLocationMentionLabel())
  }

  function renderEditor(body: string, preserveSelection = false) {
    const el = editorEl.value
    const previous = mentionDocument.display
    const start = el?.selectionStart ?? 0
    const end = el?.selectionEnd ?? start
    mentionDocument.setBody(body)
    editorText.value = mentionDocument.display
    if (el && el.value !== mentionDocument.display) el.value = mentionDocument.display
    if (el && preserveSelection && isFocused.value) {
      const next = mentionDocument.display
      let changeStart = 0
      while (
        changeStart < Math.min(previous.length, next.length) &&
        previous[changeStart] === next[changeStart]
      )
        changeStart++
      let changeEnd = previous.length
      let nextEnd = next.length
      while (
        changeEnd > changeStart &&
        nextEnd > changeStart &&
        previous[changeEnd - 1] === next[nextEnd - 1]
      ) {
        changeEnd--
        nextEnd--
      }
      const offset = (position: number) =>
        position <= changeStart
          ? position
          : position >= changeEnd
            ? position + next.length - previous.length
            : nextEnd
      el.setSelectionRange(offset(start), offset(end))
    }
    for (const segment of parseBody(body)) {
      if (
        segment.type === "mention" &&
        segment.kind === "location" &&
        !loadedLocationMentionIds.has(segment.id)
      )
        ensureLoaded(segment.id)
    }
  }

  function getCaretOffset(): number {
    return editorEl.value?.selectionStart ?? mentionDocument.display.length
  }

  function mentionAtPointer(event: MouseEvent) {
    const display = rootEl.value?.querySelector<HTMLElement>('[data-slot="mention-display"]')
    if (!display) return null
    const bounds = display.getBoundingClientRect()
    if (
      event.clientX < bounds.left ||
      event.clientX >= bounds.right ||
      event.clientY < bounds.top ||
      event.clientY >= bounds.bottom
    )
      return null
    const label = Array.from(display.querySelectorAll<HTMLElement>("[data-mention-start]")).find(
      (element) =>
        Array.from(element.getClientRects()).some(
          (rect) =>
            event.clientX >= rect.left &&
            event.clientX < rect.right &&
            event.clientY >= rect.top &&
            event.clientY < rect.bottom
        )
    )
    return label ? mentionDocument.mentionAt(Number(label.dataset.mentionStart)) : null
  }

  function handleMentionClick(event: MouseEvent, doubleClick = false) {
    const el = editorEl.value
    if (!el || (!doubleClick && (event.detail > 1 || el.selectionStart !== el.selectionEnd))) return
    const clicked = mentionAtPointer(event)
    if (!clicked) return
    if (clicked.kind === "location" && isPendingLocationId(clicked.id)) return
    if (doubleClick && clicked.kind !== "location") return
    event.stopPropagation()
    closeMenu()
    if (clicked.kind === "location") {
      if (doubleClick) mention.handleLocationMentionDoubleClick(clicked.id, event)
      else mention.handleLocationMentionClick(clicked.id, event)
    } else mention.openMention(clicked.kind, clicked.id, event)
  }

  function openMentionAtCaret(event: KeyboardEvent): boolean {
    const clicked = mentionDocument.mentionAt(getCaretOffset())
    if (!clicked) return false
    closeMenu()
    mention.openMention(clicked.kind, clicked.id, event)
    return true
  }

  function setCaretByDisplayOffset(offset: number) {
    editorEl.value?.setSelectionRange(offset, offset)
  }

  function handleBeforeInput() {
    const el = editorEl.value
    inputSelection = el ? { start: el.selectionStart, end: el.selectionEnd } : undefined
  }

  function getEditorBody(): string {
    if (!isFocused.value) return options.text.value ?? ""
    return walkEditor("body")
  }

  function replaceDisplayRange(displayStart: number, displayEnd: number, replacement: string) {
    mentionDocument.replace(displayStart, displayEnd, replacement)
    const next = mentionDocument.body
    options.text.value = next
    renderEditor(next)
    return next
  }

  const mentionTrailingSpace = " "

  function caretAfterMention(replaceStart: number, displayLabelLength: number): number {
    return replaceStart + displayLabelLength + mentionTrailingSpace.length
  }

  function insertTextAtSelection(value: string) {
    const el = editorEl.value
    if (!el) return
    const start = el.selectionStart
    mentionDocument.replace(start, el.selectionEnd, value)
    editorText.value = mentionDocument.display
    el.value = mentionDocument.display
    setCaretByDisplayOffset(start + value.length)
  }

  function revertDoubleSpaceSlashIfNeeded() {
    const trigger = locationTrigger.value
    const slashPos = slashStart.value
    if (trigger !== "double-space" || slashPos == null || !editorEl.value) return
    if (query.value.trim().length > 0) return

    const displayText = walkEditor("display")
    if (displayText[slashPos] !== LOCATION_TRIGGER) return
    if (slashPos <= 0 || displayText[slashPos - 1] !== " ") return

    replaceDisplayRange(slashPos, slashPos + 1, " ")
    setCaretByDisplayOffset(slashPos + 1)
  }

  function closeMenu() {
    revertDoubleSpaceSlashIfNeeded()
    open.value = false
    items.value = []
    query.value = ""
    highlightedIndex.value = 0
    slashStart.value = null
    slashEnd.value = null
    locationTrigger.value = null
    selectorMode.value = null
    atMentionMode.value = "contact"
    rentalProjectId.value = null
    atSuggestionLimit.value = AT_MENTION_AUTOCOMPLETE_LIMIT
    atItems.value = []
    pendingMentionAction.value = null
    delayedSearch.cancel()
  }

  function trackKeypress(event: KeyboardEvent) {
    const key = event.key
    if (shouldClearRecentKeys(key)) {
      recentKeys.value = []
      return
    }
    if (event.ctrlKey || event.metaKey || event.altKey || key.length !== 1) return

    const keysBefore = recentKeys.value
    const action = getKeypressMentionAction(
      key,
      keysBefore,
      open.value && (selectorMode.value === "location" || selectorMode.value === "at")
    )
    recentKeys.value = pushRecentKey(keysBefore, key)
    if (action) pendingMentionAction.value = action
  }

  function openLocationSession(displayText: string, cursor: number, trigger: LocationTrigger) {
    const start = cursor - 1
    selectorMode.value = "location"
    locationTrigger.value = trigger
    slashStart.value = start
    slashEnd.value = cursor
    query.value = getLocationSessionQuery(displayText, cursor, start)
    open.value = true
    delayedSearch.schedule()
  }

  /**
   * `@locatie <query>`: search locations for the query, like typing `/`. The command stays in the
   * text until a location is picked, which replaces it.
   */
  function continueLocationCommandSession(start: number, cursor: number, query: string) {
    locationTrigger.value = "command"
    // Its list shows saved locations right away; show "Zoeken…" until the search is done.
    loading.value = true
    continueSession("location", start, cursor, query)
  }

  /** Turn a double-space gesture into ` /` and open location search on the slash. */
  function openLocationSessionFromDoubleSpace(cursor: number) {
    const replaceStart = cursor - 2
    replaceDisplayRange(replaceStart, cursor, " /")
    const newCursor = replaceStart + 2

    selectorMode.value = "location"
    locationTrigger.value = "double-space"
    slashStart.value = replaceStart + 1
    slashEnd.value = newCursor
    query.value = ""
    open.value = true
    setCaretByDisplayOffset(newCursor)
    delayedSearch.schedule()
  }

  function closeMentionSessionByDoubleSpace(displayText: string, cursor: number) {
    closeMenu()
    if (cursor > 0 && isSpaceChar(displayText[cursor - 1])) {
      replaceDisplayRange(cursor - 1, cursor, "")
      setCaretByDisplayOffset(cursor - 1)
    }
  }

  /** Refresh an open (or newly-detected) session's query + selector, and re-search. */
  function continueSession(
    mode: SelectorMode,
    start: number,
    cursor: number,
    sessionQuery: string
  ) {
    selectorMode.value = mode
    slashStart.value = start
    slashEnd.value = cursor
    query.value = sessionQuery
    if (mode === "at") {
      locationTrigger.value = null
      const sessionMode = parseAtMentionQuery(sessionQuery).mode
      if (sessionMode !== atMentionMode.value) {
        // Rows of the previous mode must not be picked (e.g. a person right after `@retour`)
        // before the search below lists those of the new mode.
        atItems.value = []
        highlightedIndex.value = 0
        loading.value = true
      }
      atMentionMode.value = sessionMode
    }
    open.value = true
    delayedSearch.schedule()
  }

  function updateMentionState(displayText: string, cursor: number) {
    const before = displayText.slice(0, cursor)
    const pending = pendingMentionAction.value
    if (pending) {
      pendingMentionAction.value = null
      if (pending.type === "close") {
        closeMentionSessionByDoubleSpace(displayText, cursor)
        return
      }
      if (pending.trigger === "double-space") {
        openLocationSessionFromDoubleSpace(cursor)
        return
      }
      openLocationSession(displayText, cursor, pending.trigger)
      return
    }

    const personCommand = getActivePersonMentionCommand(before)
    const locationCommand = getActiveLocationMentionCommand(before)
    const activeCommand =
      personCommand && locationCommand
        ? personCommand.start >= locationCommand.start
          ? personCommand
          : locationCommand
        : (personCommand ?? locationCommand)

    if (activeCommand === personCommand && personCommand) {
      const createMode = parseAtMentionCreateIntent(personCommand.query)
      if (createMode) {
        triggerAtMentionCreate(personCommand.start, cursor, createMode)
        return
      }
      const parsed = parseAtMentionQuery(personCommand.query)
      if (parsed.mode === "location") {
        continueLocationCommandSession(personCommand.start, cursor, parsed.search)
        return
      }
      continueSession("at", personCommand.start, cursor, personCommand.query)
      return
    }

    if (activeCommand === locationCommand && locationCommand) {
      locationTrigger.value = "slash"
      continueSession("location", locationCommand.start, cursor, locationCommand.query)
      return
    }

    if (open.value && selectorMode.value === "at" && slashStart.value != null) {
      if (!isActivePersonMentionSession(displayText, cursor, slashStart.value)) {
        closeMenu()
        return
      }
      const sessionQuery = displayText.slice(slashStart.value + 1, cursor)
      if (!isValidMentionQuery(sessionQuery)) {
        closeMenu()
        return
      }
      const createMode = parseAtMentionCreateIntent(sessionQuery)
      if (createMode) {
        triggerAtMentionCreate(slashStart.value, cursor, createMode)
        return
      }
      const parsed = parseAtMentionQuery(sessionQuery)
      if (parsed.mode === "location") {
        continueLocationCommandSession(slashStart.value, cursor, parsed.search)
        return
      }
      continueSession("at", slashStart.value, cursor, sessionQuery)
      return
    }

    if (open.value && locationTrigger.value && slashStart.value != null) {
      // An active `@locatie` session is continued above, as an `@` command.
      if (locationTrigger.value === "command") {
        closeMenu()
        return
      }
      if (
        !isActiveLocationMentionSession(
          displayText,
          cursor,
          slashStart.value,
          locationTrigger.value
        )
      ) {
        closeMenu()
        return
      }
      const sessionQuery = getLocationSessionQuery(displayText, cursor, slashStart.value)
      if (!isValidMentionQuery(sessionQuery)) {
        closeMenu()
        return
      }
      continueSession("location", slashStart.value, cursor, sessionQuery)
      return
    }

    if (open.value) closeMenu()
  }

  function handleInput(event: Event) {
    if (!editorEl.value) {
      closeMenu()
      return
    }
    mentionDocument.edit(editorEl.value.value, inputSelection, (event as InputEvent).inputType)
    inputSelection = undefined
    editorText.value = mentionDocument.display
    const displayText = walkEditor("display")
    const cursor = getCaretOffset()

    const fileTrigger = getFileTriggerAt(displayText, cursor)
    if (fileTrigger != null) {
      replaceDisplayRange(fileTrigger, fileTrigger + 2, "")
      setCaretByDisplayOffset(fileTrigger)
      pendingFileOffset.value = fileTrigger
      closeMenu()
      options.text.value = walkEditor("body")
      options.onInput(event)
      options.onFileTrigger()
      return
    }

    const previousQuery = query.value
    updateMentionState(displayText, cursor)

    tryApplyFirstSuggestionStreet(walkEditor("display"), getCaretOffset())
    clearOutdatedLocationSuggestions(previousQuery)

    options.text.value = walkEditor("body")
    options.onInput(event)
  }

  /**
   * Drop location suggestions of an earlier query once the query changes, so the list shows only
   * the "Zoeken…" indicator until the new results are in. Runs after
   * {@link tryApplyFirstSuggestionStreet}, which still needs the highlighted suggestion.
   */
  function clearOutdatedLocationSuggestions(previousQuery: string) {
    if (!open.value || selectorMode.value !== "location") return
    // A space does not change the search, and the house number typed after it still completes
    // the highlighted street (see tryApplyFirstSuggestionStreet).
    if (trimmedQuery.value === previousQuery.trim()) return
    // Short `/` queries are not searched (the dropdown shows a hint instead).
    const willSearch = trimmedQuery.value.length >= MIN_CHARS || locationTrigger.value === "command"
    // Saved locations already known to match are shown right away, before the new results.
    items.value = willSearch
      ? filterSavedLocationSuggestions(knownSavedLocations.values(), trimmedQuery.value)
      : []
    highlightedIndex.value = 0
    loading.value = willSearch
  }

  /** Saved locations fetched in this editor, shown right away when they match a new query. */
  const knownSavedLocations = new Map<string, LocationSuggestion>()

  function rememberSavedLocations(suggestions: LocationSuggestion[]) {
    for (const suggestion of suggestions) {
      if (suggestion.source === "saved") knownSavedLocations.set(suggestion.key, suggestion)
    }
  }

  function tryApplyFirstSuggestionStreet(displayText: string, cursor: number) {
    if (!open.value || selectorMode.value !== "location" || slashStart.value == null) return
    if (items.value.length === 0) return

    const lastKey = recentKeys.value.at(-1)
    if (!lastKey || !/^\d$/.test(lastKey)) return

    const replaceStart =
      locationTrigger.value === "command"
        ? getLocationCommandQueryStart(displayText, slashStart.value)
        : slashStart.value + 1
    const sessionQuery = displayText.slice(replaceStart, cursor)
    const queryBeforeLastKey = sessionQuery.slice(0, -lastKey.length)
    if (!shouldApplyFirstSuggestionStreet(sessionQuery, queryBeforeLastKey)) return

    const highlighted = items.value[highlightedIndex.value] ?? items.value[0]
    if (!highlighted) return

    const newSessionQuery = mergeFirstSuggestionStreetWithHouseNumber(sessionQuery, highlighted)
    if (!newSessionQuery || newSessionQuery === sessionQuery) return

    const newCursor = replaceStart + newSessionQuery.length
    replaceDisplayRange(replaceStart, cursor, newSessionQuery)
    setCaretByDisplayOffset(newCursor)
    query.value = newSessionQuery
    slashEnd.value = newCursor
    delayedSearch.schedule()
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.isComposing) return
    trackKeypress(event)

    if (!open.value) return

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault()
        const delta = event.key === "ArrowDown" ? 1 : -1
        const count = (selectorMode.value === "at" ? atItems : items).value.length
        if (count) highlightedIndex.value = (highlightedIndex.value + delta + count) % count
        if (selectorMode.value === "at" && delta === 1 && highlightedIndex.value === count - 1) {
          void loadMoreAtSuggestions()
        }
        break
      }
      case "Escape":
        event.preventDefault()
        closeMenu()
        break
      case "Enter":
      case "Tab":
        event.preventDefault()
        void selectHighlightedOrFirstSuggestion()
        break
    }
  }

  function handlePaste(event: ClipboardEvent) {
    recentKeys.value = []
    // Native textareas paste URLs and rich clipboard content as plain text, and retain native undo.
    if (!options.transformClipboard) return
    event.preventDefault()
    const pasted = event.clipboardData?.getData("text/plain") ?? ""
    const plainText = options.transformClipboard?.(pasted) ?? pasted
    if (editorEl.value?.maxLength !== undefined && editorEl.value.maxLength >= 0) {
      const el = editorEl.value
      const available = Math.max(
        0,
        el.maxLength - (el.value.length - (el.selectionEnd - el.selectionStart))
      )
      insertTextAtSelection(plainText.slice(0, available))
    } else insertTextAtSelection(plainText)
    handleInput(new Event("input"))
  }

  function handleFocusout() {
    window.setTimeout(() => {
      if (!rootEl.value?.contains(document.activeElement)) {
        isFocused.value = false
        // Apply external changes (such as dropped links) deferred while the editor had focus.
        renderEditor(options.text.value ?? "")
        recentKeys.value = []
        if (open.value) closeMenu()
      }
    })
  }

  async function runSearch(): Promise<LocationSuggestion[] | AtMentionSuggestion[]> {
    if (selectorMode.value === "at") {
      return runAtSearch()
    }

    const q = trimmedQuery.value
    // `@locatie` lists saved locations right away; Maps suggestions follow from MIN_CHARS on.
    const isShortCommandSearch = q.length < MIN_CHARS && locationTrigger.value === "command"
    if (q.length < MIN_CHARS && !isShortCommandSearch) {
      items.value = []
      loading.value = false
      return []
    }
    loading.value = true
    const isCurrentSearch = () =>
      trimmedQuery.value === q && open.value && selectorMode.value === "location"
    const results = isShortCommandSearch
      ? await fetchSavedLocations(locationController, q)
      : await searchLocationSuggestions(q, {
          locationController,
          apiKey:
            options.apiKey?.value ??
            activeUser.value?.activeTenant?.tenantSettings?.apiKeyGoogleMaps ??
            "",
          // Show each part as soon as it is in, saved locations on top of the Maps suggestions.
          onPartialResults: (partial, savedPending) => {
            rememberSavedLocations(partial)
            if (!isCurrentSearch()) return
            // Until the searched saved locations are in, keep the known ones already shown.
            const shownSaved = savedPending
              ? items.value.filter((item) => item.source === "saved")
              : []
            items.value = [...shownSaved, ...partial]
          }
        })
    rememberSavedLocations(results)
    if (!isCurrentSearch()) return []
    items.value = results
    atItems.value = []
    // Keep the highlight when saved locations were already shown (they stay on top).
    if (highlightedIndex.value >= results.length) highlightedIndex.value = 0
    loading.value = false
    return results
  }

  async function runAtSearch(): Promise<AtMentionSuggestion[]> {
    const q = trimmedQuery.value
    loading.value = true
    const parsed = parseAtMentionQuery(q)
    atMentionMode.value = parsed.mode
    atSuggestionLimit.value = atMentionPageSize(parsed.mode)
    if (parsed.mode === "rental") return runRentalSearch(q, parsed.search)

    const results = searchLocalAtSuggestions(q)
    if (trimmedQuery.value !== q || !open.value || selectorMode.value !== "at") return []
    atItems.value = results
    items.value = []
    highlightedIndex.value = 0
    loading.value = false
    return results
  }

  /** Store suggestions for `q`; without a command prefix, the matching `@` commands come first. */
  function searchLocalAtSuggestions(q: string): AtMentionSuggestion[] {
    const suggestions = searchAtMentionSuggestions(
      q,
      activeKnownBusinessEntitiesContacts.value,
      projects.value,
      knownBusinessEntities.value,
      atSuggestionLimit.value
    )
    if (parseAtMentionQuery(q).hasPrefix) return suggestions
    return [...searchAtMentionCommands(q), ...suggestions]
  }

  function isCurrentAtSearch(q: string): boolean {
    return trimmedQuery.value === q && open.value && selectorMode.value === "at"
  }

  /** Project tagged before the open `@` session (see {@link findTaggedProjectId}). */
  function findSessionProjectId(): string | null {
    const body = getEditorBody()
    const start = slashStart.value
    const bodyOffset = start == null ? body.length : mentionDocument.bodyOffset(start)
    return findTaggedProjectId(body, bodyOffset)
  }

  function resolveProject(id: string): Project | null {
    return mention.resolveEntity("project", id) as Project | null
  }

  function showRentalSuggestions(search: string, projectId: string | null, keepHighlight = false) {
    atItems.value = searchRentalIntakeMentionSuggestions(
      search,
      returnableRentalIntakes.value,
      projectId ? resolveProject(projectId) : null,
      resolveProject,
      atSuggestionLimit.value
    )
    items.value = []
    if (!keepHighlight) highlightedIndex.value = 0
  }

  /**
   * Fetch the next batch of returnable intakes matching `search` into the rental intake store,
   * like the processing combobox does. The server leaves out intakes it already sent this
   * session, so repeating the request pages through them. Returns false when the request failed.
   */
  async function fetchReturnableRentalIntakes(
    projectId: string | null,
    search: string,
    cacheKey: string
  ): Promise<boolean> {
    if (!rentalIntakeController) {
      exhaustedRentalSearchKeys.add(cacheKey)
      return true
    }
    const project = projectId ? resolveProject(projectId) : null
    try {
      const batch = await rentalIntakeController.getReturnableIssuancesList(
        search,
        project ?? undefined
      )
      if (batch.length === 0) exhaustedRentalSearchKeys.add(cacheKey)
      return true
    } catch {
      return false
    }
  }

  function rentalSearchCacheKey(projectId: string | null, search: string): string {
    // Shares the combobox's cache of retrieved searches, with its own key.
    return `mention-${projectId ?? "none"}-${search}`
  }

  /**
   * `@retour <search>`: outstanding rental intakes matching the search, ranked by the tagged
   * project if any. Shows the returnable intakes already in the rental intake store right away,
   * then completes them with the server's first batch.
   */
  async function runRentalSearch(q: string, search: string): Promise<AtMentionSuggestion[]> {
    const projectId = findSessionProjectId()
    rentalProjectId.value = projectId
    showRentalSuggestions(search, projectId)

    const cacheKey = rentalSearchCacheKey(projectId, search.trim())
    if (!retrievedSearchStrings.value.has(cacheKey)) {
      retrievedSearchStrings.value.add(cacheKey)
      if (!(await fetchReturnableRentalIntakes(projectId, search.trim(), cacheKey))) {
        // Allow retrying the same search.
        retrievedSearchStrings.value.delete(cacheKey)
      }
      if (!isCurrentAtSearch(q)) return []
      showRentalSuggestions(search, projectId)
    }

    loading.value = false
    return atItems.value
  }

  /** Show the next page of the open `@` list (on scrolling to, or arrowing onto, its end). */
  async function loadMoreAtSuggestions() {
    if (!open.value || selectorMode.value !== "at" || loading.value) return
    if (atMentionMode.value === "rental") return loadMoreRentalSuggestions()

    // A list shorter than its limit already shows every match.
    const shownCount = atItems.value.filter((item) => !item.command).length
    if (shownCount < atSuggestionLimit.value) return
    atSuggestionLimit.value += atMentionPageSize(atMentionMode.value)
    atItems.value = searchLocalAtSuggestions(trimmedQuery.value)
  }

  /** Next `@retour` page; fetches the server's next batch when the loaded intakes cannot fill it. */
  async function loadMoreRentalSuggestions() {
    const projectId = rentalProjectId.value
    const q = trimmedQuery.value
    const search = parseAtMentionQuery(q).search.trim()
    const cacheKey = rentalSearchCacheKey(projectId, search)
    // All matches are shown already and the server has nothing left.
    const isPageFull = atItems.value.length >= atSuggestionLimit.value
    if (!isPageFull && exhaustedRentalSearchKeys.has(cacheKey)) return

    atSuggestionLimit.value += RENTAL_MENTION_PAGE_SIZE
    showRentalSuggestions(search, projectId, true)
    if (atItems.value.length >= atSuggestionLimit.value) return
    if (exhaustedRentalSearchKeys.has(cacheKey)) return

    loading.value = true
    await fetchReturnableRentalIntakes(projectId, search, cacheKey)
    if (!isCurrentAtSearch(q)) return
    showRentalSuggestions(search, projectId, true)
    loading.value = false
  }

  function insertAtMentionAtRange(replaceStart: number, end: number, item: AtMentionSuggestion) {
    if (item.mode === "rental") {
      const text = `${item.insertText ?? item.label}${mentionTrailingSpace}`
      const nextBody = replaceDisplayRange(replaceStart, end, text)
      setCaretByDisplayOffset(replaceStart + text.length)
      options.text.value = isFocused.value ? walkEditor("body") : nextBody
      options.onInput(new Event("input"))
      return
    }
    if (item.mode === "location") return

    const kind = MODE_META[item.mode].kind
    const label = mention.labelFor(kind, item.key, item.label)
    const nextBody = replaceDisplayRange(
      replaceStart,
      end,
      `${formatMentionToken(kind, item.key, label)}${mentionTrailingSpace}`
    )
    setCaretByDisplayOffset(caretAfterMention(replaceStart, label.length))
    options.text.value = isFocused.value ? walkEditor("body") : nextBody
    options.onInput(new Event("input"))
  }

  function findContactForPerson(personId: string): KnownBusinessEntityContact | null {
    return (
      knownBusinessEntitiesContacts.value.find((contact) => contact.person?.id === personId) ??
      activeKnownBusinessEntitiesContacts.value.find(
        (contact) => contact.person?.id === personId
      ) ??
      null
    )
  }

  /** Suggestion for a just-created entity: the saved model when it matches, else a lookup. */
  function resolveAtMentionSuggestion(
    pending: PendingAtMentionCreate,
    savedModel?: BaseModel
  ): AtMentionSuggestion | null {
    const meta = MODE_META[pending.mode]
    if (savedModel && savedModel.getClassName() === meta.savedClassName()) {
      if (!savedModel.id) return null
      const label =
        pending.mode === "contact"
          ? contactMentionLabel(savedModel as KnownBusinessEntityContact)
          : pending.mode === "project"
            ? (savedModel as Project).getMentionLabel().trim()
            : (savedModel as KnownBusinessEntity).name?.trim() || meta.fallback
      return { key: savedModel.id, label, mode: pending.mode }
    }

    if (pending.mode === "contact") {
      const contact = findContactForPerson(pending.entityId)
      if (!contact?.id) return null
      return { key: contact.id, label: contactMentionLabel(contact), mode: "contact" }
    }

    const entity =
      (ModelManager.getInstance().get(pending.entityId) as
        Project | KnownBusinessEntity | undefined) ??
      (pending.mode === "project"
        ? projects.value.find((item) => item.id === pending.entityId)
        : knownBusinessEntities.value.find((item) => item.id === pending.entityId))
    return {
      key: pending.entityId,
      label:
        pending.mode === "project"
          ? (entity as Project | undefined)?.getMentionLabel().trim() || meta.fallback
          : (entity as KnownBusinessEntity | undefined)?.name?.trim() || meta.fallback,
      mode: pending.mode
    }
  }

  function insertCreatedAtMention(pending: PendingAtMentionCreate, savedModel?: BaseModel) {
    const item = resolveAtMentionSuggestion(pending, savedModel)
    if (!item?.key) return

    pendingAtMentionCreate.value = null
    const { offset } = pending

    // Defer until after the model form unmounts so the editor DOM is stable again.
    nextTick(() => {
      requestAnimationFrame(() => {
        insertAtMentionAtRange(offset, offset, item)
        nextTick(focus)
      })
    })
  }

  function openCreateFormForAtMention(mode: CreatableAtMentionMode, offset: number) {
    const model = MODE_META[mode].createModel()
    if (!model.id) return

    const pending: PendingAtMentionCreate = { offset, mode, entityId: model.id }
    pendingAtMentionCreate.value = pending
    let savedForInsert: { pending: PendingAtMentionCreate; savedModel?: BaseModel } | null = null

    modelFormStore.openForm({
      model,
      saveHandle: (savedModel?: BaseModel) => {
        savedForInsert = { pending, savedModel }
      },
      closeHandle: () => {
        if (savedForInsert) {
          const { pending: insertPending, savedModel } = savedForInsert
          savedForInsert = null
          insertCreatedAtMention(insertPending, savedModel)
          return
        }
        if (pendingAtMentionCreate.value === pending) {
          pendingAtMentionCreate.value = null
        }
        nextTick(focus)
      }
    })
  }

  function triggerAtMentionCreate(start: number, end: number, mode: CreatableAtMentionMode) {
    const displayText = walkEditor("display")
    const replaceEnd = Math.min(end, displayText.length)
    closeMenu()
    const nextBody = replaceDisplayRange(start, replaceEnd, "")
    options.text.value = nextBody
    options.onInput(new Event("input"))
    openCreateFormForAtMention(mode, start)
  }

  /** Pick a suggestion; for a command suggestion, `command` may pick its create variant instead. */
  function selectAtSuggestion(item: AtMentionSuggestion, command = item.command) {
    const storedStart = slashStart.value
    const storedEnd = slashEnd.value
    if (storedStart == null || storedEnd == null || !item.key) {
      closeMenu()
      return
    }

    const displayText = walkEditor("display")
    const cursor = getCaretOffset()
    const end = Math.min(cursor, storedEnd, displayText.length)

    if (command) {
      selectAtMentionCommand(storedStart, end, command)
      return
    }
    closeMenu()
    insertAtMentionAtRange(storedStart, end, item)
  }

  /**
   * Type a picked `@` command for the user. A create command (e.g. `@project+`) opens its form, like
   * typing it does; a search command (e.g. `@project`) continues the session in that mode.
   */
  function selectAtMentionCommand(start: number, end: number, command: string) {
    const createMode = parseAtMentionCreateIntent(command)
    if (createMode) {
      triggerAtMentionCreate(start, end, createMode)
      return
    }
    closeMenu()
    const sessionQuery = `${command} `
    const nextBody = replaceDisplayRange(start, end, `@${sessionQuery}`)
    const cursor = start + 1 + sessionQuery.length
    setCaretByDisplayOffset(cursor)
    options.text.value = isFocused.value ? walkEditor("body") : nextBody
    options.onInput(new Event("input"))
    if (parseAtMentionQuery(command).mode === "location") {
      continueLocationCommandSession(start, cursor, "")
      return
    }
    continueSession("at", start, cursor, sessionQuery)
  }

  async function resolveLocation(name: string, address: string): Promise<Location | null> {
    if (!locationController) return null
    const trimmedName = name.trim()
    const trimmedAddress = address.trim()
    try {
      const query = new ODataQueryBuilder().ne("name", null).top(1)
      if (trimmedName) {
        query.eq("name", trimmedName)
      } else if (trimmedAddress) {
        query.eq("address", trimmedAddress)
      }
      const found = await locationController.getList({ query })
      if (found[0]?.id) return found[0]
    } catch {
      /* fall through */
    }
    try {
      // The address is geocoded server-side on create, so the location comes
      // back with coordinates without the client needing to resolve them.
      const location = new Location({
        name: trimmedName || null,
        address: trimmedAddress || null,
        isTenantBase: false
      })
      return (await locationController.create(location)) ?? null
    } catch {
      return null
    }
  }

  function createPendingLocationId(label: string): string {
    const id = uuidv4()
    pendingLocationIds.add(id)
    pendingLocationLabels.set(id, stripCountryFromLocationText(label))
    return id
  }

  function replaceLocationMentionId(pendingId: string, realId: string) {
    clearPendingLocation(pendingId)
    const body = options.text.value ?? ""
    const token = `{{location:${pendingId}}}`
    if (!body.includes(token)) return

    const nextBody = body.replace(token, `{{location:${realId}}}`)
    options.text.value = nextBody
    // Parent v-model props update on the next Vue render; use the new body immediately.
    renderEditor(nextBody, true)
    ensureLoaded(realId)
    options.onInput(new Event("input"))
  }

  function replaceLocationMentionWithText(pendingId: string, text: string) {
    clearPendingLocation(pendingId)
    const body = options.text.value ?? ""
    const token = `{{location:${pendingId}}}`
    if (!body.includes(token)) return

    const nextBody = body.replace(token, text)
    options.text.value = nextBody
    renderEditor(nextBody, true)
    options.onInput(new Event("input"))
  }

  async function finalizePendingLocationMention(pendingId: string, item: LocationSuggestion) {
    const label = stripCountryFromLocationText(item.label)
    try {
      const location = await resolveLocation("", label)
      if (!location?.id) {
        replaceLocationMentionWithText(pendingId, label)
        return
      }
      ModelManager.getInstance().updateOrCreate(Location, location)
      replaceLocationMentionId(pendingId, location.id)
    } catch {
      replaceLocationMentionWithText(pendingId, label)
    }
  }

  function insertLocationMentionAtRange(replaceStart: number, end: number, locationId: string) {
    const labelLength = mention.labelFor("location", locationId).length
    replaceDisplayRange(replaceStart, end, `{{location:${locationId}}}${mentionTrailingSpace}`)
    setCaretByDisplayOffset(caretAfterMention(replaceStart, labelLength))
    options.text.value = walkEditor("body")
    options.onInput(new Event("input"))
    nextTick(focus)
  }

  function selectSuggestion(item: LocationSuggestion) {
    const storedStart = slashStart.value
    const storedEnd = slashEnd.value
    if (storedStart == null || storedEnd == null) {
      closeMenu()
      return
    }

    const displayText = walkEditor("display")
    const cursor = getCaretOffset()
    const end = Math.min(cursor, storedEnd, displayText.length)

    open.value = false
    items.value = []
    query.value = ""
    slashStart.value = null
    slashEnd.value = null
    locationTrigger.value = null

    if (item.source === "saved" && item.location?.id) {
      insertLocationMentionAtRange(storedStart, end, item.location.id)
      return
    }

    const pendingId = createPendingLocationId(item.label)
    insertLocationMentionAtRange(storedStart, end, pendingId)
    void finalizePendingLocationMention(pendingId, item)
  }

  async function selectHighlightedOrFirstSuggestion() {
    if (selectorMode.value === "at") {
      let item = atItems.value[highlightedIndex.value] ?? atItems.value[0]
      if (!item && trimmedQuery.value.length >= AT_MENTION_AUTOCOMPLETE_MIN_CHARS) {
        delayedSearch.cancel()
        const results = await runAtSearch()
        item = results[0]
      }
      if (item) selectAtSuggestion(item)
      return
    }

    let item = items.value[highlightedIndex.value] ?? items.value[0]
    if (!item && trimmedQuery.value.length >= MIN_CHARS) {
      delayedSearch.cancel()
      const results = (await runSearch()) as LocationSuggestion[]
      item = results[0]
    }
    if (item) selectSuggestion(item)
  }

  function onMapSaved(saved: Location) {
    mention.closeMap()
    if (saved.id) refreshLocationMentionLabel()
    if (!isFocused.value) renderEditor(options.text.value ?? "")
  }

  function focus() {
    editorEl.value?.focus()
  }

  function insertUploadedFile(fileRelation: FileRelation) {
    const offset = pendingFileOffset.value
    pendingFileOffset.value = null
    if (offset == null || !fileRelation?.id) return
    const labelLength = mention.labelFor("file", fileRelation.id).length
    replaceDisplayRange(offset, offset, `{{file:${fileRelation.id}}}${mentionTrailingSpace}`)
    setCaretByDisplayOffset(caretAfterMention(offset, labelLength))
    options.text.value = walkEditor("body")
    options.onInput(new Event("input"))
  }

  return {
    MIN_CHARS,
    rootEl,
    editorEl,
    displayText: editorText,
    displaySegments: computed(() => {
      void editorText.value
      void options.text.value
      return mentionDocument.displaySegments
    }),
    mentionLinkClass: mention.mentionLinkClass,
    mentionTitle: mention.mentionTitle,
    mentionAtPointer,
    handleBeforeInput,
    handleMentionClick,
    openMentionAtCaret,
    closeMenu,
    isFocused,
    open,
    loading,
    items,
    atItems,
    atMentionMode,
    rentalProject,
    locationTrigger,
    selectorMode,
    highlightedIndex,
    trimmedQuery,
    mapLocation: mention.mapLocation,
    mapAnchor: mention.mapAnchor,
    closeMap: mention.closeMap,
    usesInlineLocationMap: mention.usesInlineLocationMap,
    onMapSaved,
    handleInput,
    handleKeydown,
    handlePaste,
    handleFocusout,
    selectSuggestion,
    selectAtSuggestion,
    loadMoreAtSuggestions,
    focus,
    insertUploadedFile
  }
}
